import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CookieNotice } from "@/components/CookieNotice";
import { openCookiePreferences } from "@/lib/consent";
import { ArrowLeft, Cookie } from "lucide-react";

const COOKIES = [
    { name: "Strictly necessary", legal: "Necessary for the service you requested", items: "Session token (keeps you signed in), security/anti-forgery, and your saved cookie-consent choice.", basis: "Always on — cannot be switched off." },
    { name: "Functional", legal: "Your consent", items: "Remembers preferences such as your typed school route so you don't have to enter it again.", basis: "Optional — only set if you accept functional cookies." },
];

export default function Privacy() {
    return (
        <div className="min-h-screen bg-brand-bone text-brand-emerald">
            <header className="sticky top-0 z-30 backdrop-blur-md bg-white/90 border-b border-border">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-3">
                    <img src="/procureed-logo.png" alt="ProcureEd" className="h-8 w-auto" />
                    <Link to="/" className="ml-auto">
                        <Button variant="ghost" className="rounded-full" data-testid="privacy-back-home"><ArrowLeft className="h-4 w-4 mr-1" /> Home</Button>
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="privacy-page">
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">Privacy &amp; Cookie Policy</h1>
                <p className="mt-2 text-sm text-muted-foreground">Last updated {new Date().getFullYear()}</p>

                <div className="mt-8 space-y-8 text-slate-700 leading-relaxed">
                    <section>
                        <p>
                            ProcureEd respects your privacy. This page explains what cookies and similar local-storage
                            technologies we use, why we use them, and how you can control them. We do not use advertising,
                            profiling or third-party tracking cookies.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-lg font-semibold text-brand-emerald">Cookies &amp; local storage we use</h2>
                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full text-sm border border-border rounded-lg overflow-hidden">
                                <thead>
                                    <tr className="bg-secondary/40 text-left text-brand-emerald">
                                        <th className="p-3 font-medium">Category</th>
                                        <th className="p-3 font-medium">What it's for</th>
                                        <th className="p-3 font-medium">Legal basis</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {COOKIES.map((c) => (
                                        <tr key={c.name} className="border-t border-border align-top">
                                            <td className="p-3 font-medium text-brand-emerald whitespace-nowrap">{c.name}</td>
                                            <td className="p-3 text-muted-foreground">{c.items}</td>
                                            <td className="p-3 text-muted-foreground">{c.legal}<div className="mt-1 text-xs">{c.basis}</div></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-lg font-semibold text-brand-emerald">Your choices</h2>
                        <p className="mt-2 text-muted-foreground">
                            When you first visit, we ask whether you accept functional cookies. Strictly necessary cookies are
                            always on because the service cannot work without them. You can change or withdraw your consent at
                            any time — your choice takes effect immediately, and withdrawing functional consent deletes the
                            related cookie from this device. You can also clear cookies in your browser settings.
                        </p>
                        <Button onClick={openCookiePreferences} className="mt-4 bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg" data-testid="privacy-manage-cookies">
                            <Cookie className="h-4 w-4 mr-1.5" /> Manage cookie preferences
                        </Button>
                    </section>

                    <section>
                        <h2 className="text-lg font-semibold text-brand-emerald">Your data &amp; contact</h2>
                        <p className="mt-2 text-muted-foreground">
                            You have the right to access, correct or delete your personal data and to object to or restrict its
                            processing. To exercise any of these rights, contact your school administrator or reach us at{" "}
                            <a href="mailto:privacy@procureed.app" className="text-brand-teal underline underline-offset-2">privacy@procureed.app</a>.
                        </p>
                    </section>
                </div>
            </main>
            <CookieNotice />
        </div>
    );
}
